'use client';

import * as React from 'react';
import * as AccordionPrimitive from '@radix-ui/react-accordion';

import { cn } from '@/hen-ui/utils';
import { ChevronDownIcon } from 'lucide-react';

const Accordion = React.forwardRef<
  React.ElementRef<typeof AccordionPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Root>
>(({ className, ...props }, ref) => (
  <AccordionPrimitive.Root
    ref={ref}
    data-slot='accordion'
    className={cn('flex w-full flex-col gap-xxs', className)}
    {...props}
  />
));

Accordion.displayName = AccordionPrimitive.Root.displayName;

const AccordionItem = React.forwardRef<
  React.ElementRef<typeof AccordionPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Item>
>(({ className, ...props }, ref) => (
  <AccordionPrimitive.Item
    ref={ref}
    data-slot='accordion-item'
    className={className}
    {...props}
  />
));

AccordionItem.displayName = AccordionPrimitive.Item.displayName;

const AccordionTrigger = React.forwardRef<
  React.ElementRef<typeof AccordionPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Trigger>
>(({ className, children, ...props }, ref) => (
  <AccordionPrimitive.Header className='flex'>
    <AccordionPrimitive.Trigger
      ref={ref}
      data-slot='accordion-trigger'
      className={cn(
        'group/accordion-trigger relative flex flex-1 p-sm items-center justify-between',
        'transition-all duration-normal ease-in-out',
        className,
      )}
      {...props}
    >
      {children}
      <ChevronDownIcon
        data-slot='accordion-trigger-icon'
        className={cn(
          'pointer-events-none shrink-0 size-xl text-neutral-600 flex items-center',
          'transition-transform duration-fast ease-in-out',
          'group-aria-[expanded=true]/accordion-trigger:rotate-180',
        )}
      />
    </AccordionPrimitive.Trigger>
  </AccordionPrimitive.Header>
));

AccordionTrigger.displayName = AccordionPrimitive.Trigger.displayName;

const AccordionContent = React.forwardRef<
  React.ElementRef<typeof AccordionPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Content>
>(({ className, children, ...props }, ref) => (
  <AccordionPrimitive.Content
    ref={ref}
    data-slot='accordion-content'
    className={cn(
      'overflow-hidden',
      'data-[state=closed]:animate-accordion-up',
      'data-[state=open]:animate-accordion-down',
    )}
    {...props}
  >
    <div className={cn('gap-sm px-sm pb-sm', className)}>{children}</div>
  </AccordionPrimitive.Content>
));

AccordionContent.displayName = AccordionPrimitive.Content.displayName;

export { Accordion, AccordionItem, AccordionTrigger, AccordionContent };
